import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone?: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  updated_at?: string;
  last_login?: string;
  can_create_events: boolean;
  can_manage_invitees: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

useEffect(() => {
  const validateExistingAuth = async () => {
    console.log('AuthProvider useEffect - Checking existing auth...');

    const token = localStorage.getItem('ffm_access_token');
    const savedUser = localStorage.getItem('ffm_user');

    console.log('Token exists:', !!token);
    console.log('Saved user exists:', !!savedUser);

    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser); // ← This is the correct variable
        console.log('Attempting to validate token for user:', parsedUser.email);

        // Validate the token is still valid by calling /auth/me
        const isValid = await apiService.validateToken();

        if (isValid) {
          console.log('Token is valid, restoring user:', parsedUser.email);
          setUser(parsedUser); // ← Use parsedUser here
        } else {
          console.log('Token is invalid, clearing auth data');
          localStorage.removeItem('ffm_access_token');
          localStorage.removeItem('ffm_user');
        }
      } catch (error) {
        console.error('Error validating auth:', error);
        localStorage.removeItem('ffm_access_token');
        localStorage.removeItem('ffm_user');
      }
    } else {
      console.log('No existing auth found');
    }

    setIsLoading(false);
  };

  validateExistingAuth();
}, []);

  const login = async (email: string, password: string) => {
    try {
      console.log('Login attempt for:', email);
      
      const loginData = {
        email: email,
        password: password
      };

      const response = await apiService.post<{
        access_token: string;
        token_type: string;
        expires_in: number;
        user: User;
      }>('/auth/login', loginData);

      const { access_token, user: userData } = response;

      console.log('Login successful for:', userData.email);
      console.log('Token received (first 20 chars):', access_token.substring(0, 20) + '...');

      // Save token and user data
      localStorage.setItem('ffm_access_token', access_token);
      localStorage.setItem('ffm_user', JSON.stringify(userData));

      console.log('Auth data saved to localStorage');

      setUser(userData);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    console.log('Logging out user');
    localStorage.removeItem('ffm_access_token');
    localStorage.removeItem('ffm_user');
    setUser(null);
  };

  // Own profile edited (#72): keep state and the stored copy in sync.
  const updateUser = (updated: User) => {
    localStorage.setItem('ffm_user', JSON.stringify(updated));
    setUser(updated);
  };

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
  };

  console.log('AuthProvider render - isAuthenticated:', !!user, 'isLoading:', isLoading, 'user:', user?.email);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};